import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Link,
  InputAdornment
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { forgotPasswordAdmin, loginAdmin } from 'src/api';
import { CONSTANTS } from 'src/commons';
import Alert from 'src/components/Alert';
import { commonReducerAction } from 'src/redux/reducers/common';
import { loaderReducerAction } from 'src/redux/reducers/loader';

const Login = () => {
  const { startLoading, stopLoading } = loaderReducerAction;
  const { updateUserCredentials, setVisible, setGymId } = commonReducerAction;
  const dispatch = useDispatch();
  const { user, visible } = useSelector((state) => state.common);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: ''
  });

  const initialValues = {
    email: '',
    password: ''
  };

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    touched,
    values,
    resetForm
  } = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email('Must be a valid email')
        .max(255)
        .required('Email is required'),
      password: showForgotPassword
        ? Yup.string().optional()
        : Yup.string().max(255).required('Password is required')
    }),
    mode: 'onSubmit',
    onSubmit: async (values) => {
      try {
        dispatch(startLoading());
        let res;
        if (showForgotPassword) {
          res = await forgotPasswordAdmin({ email: values?.email });
        } else {
          res = await loginAdmin(values);
        }
        const isError = res.status !== 200;
        dispatch(stopLoading());
        setSnackbarState({
          open: true,
          message: res.message,
          severity: isError ? 'error' : 'success'
        });

        if (!isError && !showForgotPassword) {
          dispatch(updateUserCredentials(res.data));
          dispatch(setGymId(res?.data?.all_gyms[0]._id));
        }
        if (!Error) {
          resetForm();
        }
      } catch (err) {
        setSnackbarState({
          open: true,
          message: err.message,
          severity: 'error'
        });
      }
    }
  });

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarState({
      ...snackbarState,
      open: false
    });
  };
  const history = useNavigate();

  useEffect(() => {
    if (user?.access_token) {
      history(CONSTANTS.ROUTES.PRIVATE_ROUTES.DASHBOARD, {
        replace: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <>
      <Helmet>
        <title>Login | GMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="sm">
          <form onSubmit={handleSubmit} noValidate>
            <Box sx={{ mb: 3 }}>
              <Typography color="textPrimary" variant="h2">
                {showForgotPassword ? 'Forgot password' : 'Sign in'}
              </Typography>
            </Box>
            <TextField
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              label="Email Address"
              margin="normal"
              name="email"
              onBlur={handleBlur}
              onChange={handleChange}
              type="email"
              value={values.email}
              variant="outlined"
            />

            {!showForgotPassword && (
              <TextField
                error={Boolean(touched.password && errors.password)}
                fullWidth
                helperText={touched.password && errors.password}
                label="Password"
                margin="normal"
                name="password"
                onBlur={handleBlur}
                onChange={handleChange}
                type={visible ? 'text' : 'password'}
                value={values.password}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        dispatch(setVisible());
                      }}
                    >
                      {visible ? (
                        <VisibilityIcon
                          sx={{
                            color: 'black',
                            height: '18px'
                          }}
                        />
                      ) : (
                        <VisibilityOffIcon
                          sx={{
                            color: 'black',
                            height: '18px'
                          }}
                        />
                      )}
                    </InputAdornment>
                  )
                }}
              />
            )}

            <Box sx={{ py: 2 }}>
              <Button
                color="primary"
                disabled={isSubmitting}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                {showForgotPassword ? 'Send email' : 'Sign in now'}
              </Button>
            </Box>
          </form>
          {showForgotPassword ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end'
              }}
            >
              <Typography color="textSecondary" variant="body1">
                Back to{' '}
                <span
                  onClick={() => {
                    setShowForgotPassword(false);
                    resetForm();
                  }}
                  style={{
                    color: 'black',
                    fontWeight: 500,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Sign in
                </span>
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography color="textSecondary" variant="body1">
                Don't Have an account?{' '}
                <Link component={RouterLink} to="/register" variant="h6">
                  Register
                </Link>
              </Typography>

              <Typography
                variant="h6"
                onClick={() => {
                  setShowForgotPassword(true);
                  resetForm();
                }}
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                Forgot password?
              </Typography>
            </Box>
          )}
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={snackbarState.open}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <div>
              <Alert
                variant="filled"
                elevation={6}
                onClose={handleClose}
                severity={snackbarState.severity}
              >
                {snackbarState.message}
              </Alert>
            </div>
          </Snackbar>
        </Container>
      </Box>
    </>
  );
};

export default Login;
